import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";

import type { ReceivedGumball, ThemeSceneProps } from "../types";

const PATH_VIEW_W = 1000;
const PATH_VIEW_H = 600;
const GUMBALL_SPEED = 0.046;
const ORBIT_PATH =
  "M -40 430 C 130 275, 295 345, 420 235 S 690 155, 805 300 S 905 510, 1040 345";

const RAINBOW_RIBBONS: ReadonlyArray<{
  color: string;
  offset: number;
  width: number;
  opacity: number;
}> = [
  { color: "#ff7adf", offset: -30, width: 9, opacity: 0.98 },
  { color: "#ffb36d", offset: -20, width: 9, opacity: 0.92 },
  { color: "#ffe779", offset: -10, width: 9, opacity: 0.96 },
  { color: "#8dffb5", offset: 0, width: 9, opacity: 0.92 },
  { color: "#78f4ff", offset: 10, width: 9, opacity: 0.98 },
  { color: "#b58cff", offset: 20, width: 9, opacity: 0.94 },
  { color: "#ff7adf", offset: 30, width: 9, opacity: 0.82 }
];

const SHIMMER_PULSES: ReadonlyArray<{ offset: number; delay: number; duration: number }> = [
  { offset: -30, delay: 0, duration: 5.6 },
  { offset: -10, delay: -2.1, duration: 6.8 },
  { offset: 10, delay: -4.2, duration: 5.1 },
  { offset: 30, delay: -1.3, duration: 7.4 }
];

const GUMBALL_TINTS: ReadonlyArray<readonly [string, string, string]> = [
  ["#ff7adf", "#ffe779", "#ffb36d"],
  ["#78f4ff", "#8dffb5", "#b58cff"],
  ["#ffe779", "#ffb36d", "#ff7adf"],
  ["#b58cff", "#78f4ff", "#ff7adf"],
  ["#8dffb5", "#78f4ff", "#ffe779"],
  ["#ffffff", "#ffd6f2", "#b58cff"]
];

const STARS = buildStars(135);
const SHOOTING_STARS = buildShootingStars();
const CLOUDS = buildClouds();
const LOLLIPOPS = buildLollipops();
const FAIRY_DUST = buildFairyDust(42);

export function UnicornLandScene({ received }: ThemeSceneProps) {
  const pathRef = useRef<SVGPathElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cardTRef = useRef<number[]>([]);
  const [pathLength, setPathLength] = useState(0);

  useEffect(() => {
    cardTRef.current = received.map((_, i) =>
      received.length === 0 ? 0 : (i / received.length + 0.12) % 1
    );
    cardRefs.current.length = received.length;
  }, [received.length]);

  useEffect(() => {
    const path = pathRef.current;
    if (path === null) {
      return;
    }
    const apply = () => setPathLength(path.getTotalLength());
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  useEffect(() => {
    const path = pathRef.current;
    if (path === null) {
      return;
    }
    let total = path.getTotalLength();
    let last = performance.now();
    let raf = 0;

    const onResize = () => {
      total = path.getTotalLength();
    };
    window.addEventListener("resize", onResize);

    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const ts = cardTRef.current;
      for (let i = 0; i < ts.length; i++) {
        ts[i] = (ts[i]! + GUMBALL_SPEED * dt) % 1;
        const point = path.getPointAtLength(ts[i]! * total);
        const el = cardRefs.current[i];
        if (el !== null && el !== undefined) {
          el.style.left = `${(point.x / PATH_VIEW_W) * 100}%`;
          el.style.top = `${(point.y / PATH_VIEW_H) * 100}%`;
        }
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [received.length]);

  const shimmerDashArray =
    pathLength > 0 ? `${pathLength * 0.035} ${pathLength * 0.965}` : undefined;
  const shimmerDashEnd = pathLength > 0 ? -pathLength : undefined;

  return (
    <div className="ul-scene" aria-hidden="true">
      <div className="ul-scene__mesh" />
      <div className="ul-scene__grain" />
      <div className="ul-scene__moon" />

      <div className="ul-scene__stars">
        {STARS.map((star, i) => (
          <span
            key={i}
            className={`ul-star ul-star--${star.kind}`}
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: star.color,
              color: star.color,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`
            }}
          />
        ))}
      </div>

      <div className="ul-scene__shooting-stars">
        {SHOOTING_STARS.map((star, i) => (
          <span
            key={i}
            className="ul-shooting-star"
            style={
              {
                left: `${star.left}%`,
                top: `${star.top}%`,
                "--ul-shoot-angle": `${star.angle}deg`,
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`
              } as CSSProperties
            }
          />
        ))}
      </div>

      <div className="ul-scene__clouds">
        {CLOUDS.map((cloud, i) => (
          <Cloud key={i} index={i} {...cloud} />
        ))}
      </div>

      <svg
        className="ul-scene__horizon"
        viewBox={`0 0 ${PATH_VIEW_W} ${PATH_VIEW_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="ul-horizon-back" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8d5cff" stopOpacity="0.42" />
            <stop offset="100%" stopColor="#17082f" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="ul-horizon-front" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff7adf" stopOpacity="0.48" />
            <stop offset="44%" stopColor="#7b57ff" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#0b0620" stopOpacity="0" />
          </linearGradient>
          <filter id="ul-horizon-blur" x="-8%" y="-20%" width="116%" height="150%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
        </defs>
        <path
          d="M0 432 C 160 370, 292 430, 470 382 S 805 430, 1000 372 L1000 600 L0 600 Z"
          fill="url(#ul-horizon-back)"
          filter="url(#ul-horizon-blur)"
        />
        <path
          d="M0 510 C 190 440, 350 500, 530 458 S 810 512, 1000 458 L1000 600 L0 600 Z"
          fill="url(#ul-horizon-front)"
        />
        <path
          d="M0 570 C 180 520, 355 550, 535 528 S 820 570, 1000 535 L1000 600 L0 600 Z"
          fill="rgba(255, 231, 121, 0.16)"
        />
      </svg>

      <svg
        className="ul-scene__rainbow"
        viewBox={`0 0 ${PATH_VIEW_W} ${PATH_VIEW_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <filter id="ul-rainbow-glow" x="-12%" y="-90%" width="124%" height="280%">
            <feGaussianBlur stdDeviation="12" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d={ORBIT_PATH}
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth="102"
          strokeLinecap="round"
          fill="none"
          filter="url(#ul-rainbow-glow)"
        />
        <path
          d={ORBIT_PATH}
          stroke="rgba(15, 6, 38, 0.76)"
          strokeWidth="72"
          strokeLinecap="round"
          fill="none"
        />
        {RAINBOW_RIBBONS.map((ribbon, i) => (
          <path
            key={i}
            d={ORBIT_PATH}
            stroke={ribbon.color}
            strokeWidth={ribbon.width}
            strokeLinecap="round"
            fill="none"
            opacity={ribbon.opacity}
            transform={`translate(0 ${ribbon.offset})`}
          />
        ))}
        <path ref={pathRef} d={ORBIT_PATH} stroke="none" fill="none" />
        {shimmerDashArray !== undefined &&
          SHIMMER_PULSES.map((pulse, i) => (
            <path
              key={i}
              d={ORBIT_PATH}
              stroke="#ffffff"
              strokeWidth="5"
              strokeLinecap="round"
              fill="none"
              strokeDasharray={shimmerDashArray}
              opacity="0.8"
              transform={`translate(0 ${pulse.offset})`}
              style={
                {
                  "--ul-dash-end": `${shimmerDashEnd}`,
                  animation: `ul-rainbow-shimmer ${pulse.duration}s linear infinite`,
                  animationDelay: `${pulse.delay}s`
                } as CSSProperties
              }
            />
          ))}
      </svg>

      <div className="ul-scene__props">
        {LOLLIPOPS.map((lollipop, i) => (
          <Lollipop key={i} index={i} {...lollipop} />
        ))}
      </div>

      <div className="ul-scene__dust">
        {FAIRY_DUST.map((dust, i) => (
          <span
            key={i}
            className="ul-dust"
            style={{
              left: `${dust.x}%`,
              top: `${dust.y}%`,
              width: `${dust.size}px`,
              height: `${dust.size}px`,
              animationDelay: `${dust.delay}s`,
              animationDuration: `${dust.duration}s`
            }}
          />
        ))}
      </div>

      <UnicornMascot />

      <div className="ul-scene__cards">
        {received.map((entry, i) => (
          <GumballCard
            key={entry.id}
            entry={entry}
            index={i}
            elRef={(el) => {
              cardRefs.current[i] = el;
            }}
          />
        ))}
      </div>
    </div>
  );
}

function Cloud({
  x,
  y,
  scale,
  delay,
  duration,
  index
}: {
  x: number;
  y: number;
  scale: number;
  delay: number;
  duration: number;
  index: number;
}) {
  const id = `ul-cloud-soft-${index}`;
  return (
    <svg
      className="ul-cloud"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${210 * scale}px`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`
      }}
      viewBox="0 0 240 112"
      aria-hidden="true"
    >
      <defs>
        <filter id={id} x="-12%" y="-16%" width="124%" height="132%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>
      <g filter={`url(#${id})`}>
        <ellipse cx="58" cy="72" rx="46" ry="25" fill="#ffe7fb" opacity="0.84" />
        <ellipse cx="108" cy="56" rx="52" ry="34" fill="#ffffff" opacity="0.92" />
        <ellipse cx="158" cy="68" rx="48" ry="27" fill="#dffbff" opacity="0.78" />
        <ellipse cx="196" cy="74" rx="34" ry="21" fill="#ffd9f4" opacity="0.72" />
        <ellipse cx="96" cy="45" rx="23" ry="13" fill="#ffffff" opacity="0.8" />
      </g>
    </svg>
  );
}

function Lollipop({
  x,
  y,
  hue,
  scale,
  index
}: {
  x: number;
  y: number;
  hue: number;
  scale: number;
  index: number;
}) {
  const c1 = `hsl(${hue}, 92%, 68%)`;
  const c2 = `hsl(${(hue + 58) % 360}, 96%, 78%)`;
  return (
    <div
      className="ul-lolli"
      style={{
        left: `${x}%`,
        bottom: `${y}%`,
        transform: `scale(${scale})`
      }}
    >
      <div className="ul-lolli__bob" style={{ animationDelay: `${index * -0.55}s` }}>
        <span
          className="ul-lolli__head"
          style={{
            background: `conic-gradient(from 20deg, ${c1}, ${c2}, ${c1}, ${c2}, ${c1})`,
            boxShadow: `0 0 28px ${c1}88, inset 5px 5px 10px rgba(255,255,255,0.62), inset -7px -8px 12px rgba(75,16,90,0.24)`
          }}
        />
        <span className="ul-lolli__stick" />
      </div>
    </div>
  );
}

function UnicornMascot() {
  return (
    <div className="ul-mascot-hero" aria-hidden="true">
      <div className="ul-mascot-hero__halo" />
      <div className="ul-mascot-hero__bob">
        <img
          className="ul-mascot-hero__image"
          src="/assets/unicorn-land-mascot.png"
          alt=""
          draggable={false}
        />
        <span className="ul-mascot-hero__glint ul-mascot-hero__glint--1" />
        <span className="ul-mascot-hero__glint ul-mascot-hero__glint--2" />
        <span className="ul-mascot-hero__glint ul-mascot-hero__glint--3" />
      </div>
    </div>
  );
}

function GumballCard({
  entry,
  index,
  elRef
}: {
  entry: ReceivedGumball;
  index: number;
  elRef: (el: HTMLDivElement | null) => void;
}) {
  const count = Math.max(1, Math.min(entry.amount, 9));
  const columns = Math.min(count, 3);
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);
  return (
    <div ref={elRef} className="ul-card">
      <div className="ul-card__bob" style={{ animationDelay: `${(index % 6) * -0.42}s` }}>
        <span className="ul-card__halo" />
        <div
          className="ul-card__cluster"
          style={{ gridTemplateColumns: `repeat(${columns}, 20px)` }}
        >
          {pieces.map((piece) => (
            <Gumball key={piece} variant={(index + piece) % GUMBALL_TINTS.length} index={index * 13 + piece} />
          ))}
        </div>
        <div className="ul-card__tooltip" role="tooltip">
          <span className="ul-card__from">{entry.from}</span>
          <span className="ul-card__amount">{entry.amount}</span>
          {entry.reason !== null && entry.reason.trim() !== "" && (
            <div className="ul-card__note">{entry.reason}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Gumball({ variant, index }: { variant: number; index: number }) {
  const [core, rim, glow] = GUMBALL_TINTS[variant % GUMBALL_TINTS.length]!;
  const id = `ul-gumball-${index}`;
  return (
    <svg className="ul-card__piece" width="20" height="20" viewBox="0 0 22 22" aria-hidden="true">
      <defs>
        <radialGradient id={id} cx="31%" cy="24%" r="76%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="24%" stopColor={core} />
          <stop offset="72%" stopColor={rim} />
          <stop offset="100%" stopColor={glow} />
        </radialGradient>
      </defs>
      <circle cx="11" cy="11" r="10" fill={`url(#${id})`} />
      <circle cx="11" cy="11" r="9.4" fill="none" stroke="rgba(255,255,255,0.52)" strokeWidth="0.65" />
      <path d="M5.1 12.2 C8.4 9.2, 13.4 9.1, 17.1 12" fill="none" stroke="rgba(255,255,255,0.34)" strokeWidth="0.7" />
      <circle cx="7.2" cy="6.4" r="2.4" fill="rgba(255,255,255,0.78)" />
    </svg>
  );
}

function buildStars(count: number) {
  const out: Array<{
    x: number;
    y: number;
    size: number;
    delay: number;
    duration: number;
    color: string;
    kind: "pin" | "flare" | "soft";
  }> = [];
  const colors = ["#ffffff", "#ffe7fb", "#dffbff", "#fff1a8", "#e8dcff"];
  let seed = 0x71c0;
  for (let i = 0; i < count; i++) {
    seed = nextSeed(seed);
    const x = (seed % 1000) / 10;
    seed = nextSeed(seed);
    const y = (seed % 760) / 10;
    seed = nextSeed(seed);
    const roll = seed % 100;
    const size = roll < 72 ? 1 + (roll % 16) / 10 : 2.2 + (roll % 30) / 10;
    seed = nextSeed(seed);
    const delay = -((seed % 90) / 10);
    seed = nextSeed(seed);
    const duration = 3.1 + (seed % 52) / 10;
    seed = nextSeed(seed);
    const color = colors[seed % colors.length]!;
    const kind = roll < 72 ? "pin" : roll < 91 ? "soft" : "flare";
    out.push({ x, y, size, delay, duration, color, kind });
  }
  return out;
}

function buildShootingStars() {
  return [
    { left: -14, top: 12, angle: -17, delay: -1.2, duration: 8.6 },
    { left: 18, top: 2, angle: -22, delay: -5.4, duration: 10.2 },
    { left: 52, top: 18, angle: -14, delay: -8.1, duration: 9.4 }
  ];
}

function buildClouds() {
  return [
    { x: -8, y: 15, scale: 0.78, delay: -18, duration: 58 },
    { x: 22, y: 8, scale: 1.0, delay: -38, duration: 72 },
    { x: 58, y: 18, scale: 0.72, delay: -9, duration: 64 },
    { x: 80, y: 30, scale: 0.62, delay: -28, duration: 54 }
  ];
}

function buildLollipops() {
  return [
    { x: 5, y: 5, hue: 318, scale: 0.9 },
    { x: 18, y: 12, hue: 52, scale: 0.7 },
    { x: 33, y: 7, hue: 178, scale: 0.82 },
    { x: 72, y: 11, hue: 222, scale: 0.74 },
    { x: 88, y: 5, hue: 286, scale: 0.92 }
  ];
}

function buildFairyDust(count: number) {
  const out: Array<{ x: number; y: number; size: number; delay: number; duration: number }> = [];
  let seed = 0xd457;
  for (let i = 0; i < count; i++) {
    seed = nextSeed(seed);
    const x = (seed % 1000) / 10;
    seed = nextSeed(seed);
    const y = 18 + (seed % 700) / 10;
    seed = nextSeed(seed);
    const size = 3 + (seed % 42) / 10;
    seed = nextSeed(seed);
    const delay = -((seed % 100) / 10);
    seed = nextSeed(seed);
    const duration = 6 + (seed % 80) / 10;
    out.push({ x, y, size, delay, duration });
  }
  return out;
}

function nextSeed(seed: number): number {
  return (seed * 1103515245 + 12345) & 0x7fffffff;
}
